import { apiRequest } from "@/lib/api/http";

export interface ClientProfile {
  title: string;
  address: string;
  zip: string;
  city: string;
}

export interface RemoteClient {
  title?: string;
  address?: string;
  zip?: string;
  city?: string;
  /** Hardware features set in the back office (`docs/CLIENT_FEATURES_SERVER.md`). */
  features?: unknown;
}

interface ClientResponse {
  success?: boolean;
  data?: RemoteClient | null;
}

export function mapClient(raw: RemoteClient): ClientProfile | null {
  const title = raw.title?.trim() ?? "";
  if (!title) return null;
  return {
    title,
    address: raw.address?.trim() ?? "",
    zip: raw.zip?.trim() ?? "",
    city: raw.city?.trim() ?? "",
  };
}

export function extractClient(payload: unknown): RemoteClient | null {
  if (!payload || typeof payload !== "object") return null;
  const record = payload as Record<string, unknown>;
  if (record.data && typeof record.data === "object" && !Array.isArray(record.data)) {
    return record.data as RemoteClient;
  }
  return record as RemoteClient;
}

/** Formats address, zip, and city for the PDF masthead second line. */
export function formatClientAddressLine(client: ClientProfile): string {
  const parts: string[] = [];
  if (client.address) parts.push(client.address);
  const locality = [client.zip, client.city].filter(Boolean).join(" ");
  if (locality) parts.push(locality);
  return parts.join(", ");
}

/**
 * The authenticated account's own client profile, or `null` when unavailable. Like the logo,
 * a missing profile is normal rather than a failure, so every error is swallowed.
 */
export async function fetchClient(): Promise<ClientProfile | null> {
  let payload: unknown;
  try {
    payload = await apiRequest<ClientResponse | RemoteClient>("/api/client", {
      method: "GET",
      cache: "no-store",
    });
  } catch {
    return null;
  }

  const raw = extractClient(payload);
  if (!raw) return null;
  return mapClient(raw);
}
